import React, { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FileText, Plus, Send, CheckCircle, XCircle, Clock, Eye,
  Building2, Calendar, DollarSign, ChevronRight, AlertTriangle
} from "lucide-react";

const JobOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Form state
  const [newOffer, setNewOffer] = useState({
    project_id: "",
    subcontractor_id: "",
    trade_phase: "",
    scope_text: "",
    proposed_start_date: "",
    proposed_end_date: "",
    price_type: "fixed",
    total_price: "",
    retainage_percent: "0",
    compliance_requirements: ["W9", "COI"],
    notes: ""
  });

  const trades = [
    "Electrical", "Plumbing", "HVAC", "Framing", "Roofing", "Flooring",
    "Drywall", "Painting", "Concrete", "Landscaping", "Demolition",
    "Insulation", "Windows & Doors", "Cabinetry", "Tile", "General Labor"
  ];

  const fetchData = useCallback(async () => {
    try {
      const [offersRes, projectsRes, subsRes] = await Promise.all([
        API.get("/job-offers"),
        API.get("/projects"),
        API.get("/subcontractors")
      ]);
      setOffers(offersRes.data);
      setProjects(projectsRes.data);
      setSubcontractors(subsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateOffer = async () => {
    if (!newOffer.project_id || !newOffer.subcontractor_id || !newOffer.trade_phase) {
      toast.error("Project, subcontractor, and trade are required");
      return;
    }
    try {
      await API.post("/job-offers", {
        ...newOffer,
        total_price: parseFloat(newOffer.total_price) || 0,
        retainage_percent: parseFloat(newOffer.retainage_percent) || 0
      });
      toast.success("Job offer created");
      setCreateDialogOpen(false);
      setNewOffer({
        project_id: "",
        subcontractor_id: "",
        trade_phase: "",
        scope_text: "",
        proposed_start_date: "",
        proposed_end_date: "",
        price_type: "fixed",
        total_price: "",
        retainage_percent: "0",
        compliance_requirements: ["W9", "COI"],
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create offer");
    }
  };

  const handleSendOffer = async (offerId) => {
    try {
      await API.post(`/job-offers/${offerId}/send`);
      toast.success("Offer sent to subcontractor");
      fetchData();
    } catch (error) {
      toast.error("Failed to send offer");
    }
  };

  const handleViewOffer = async (offer) => {
    try {
      const response = await API.get(`/job-offers/${offer.offer_id}`);
      setSelectedOffer(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load offer details");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: "bg-slate-100 text-slate-600", icon: Clock, label: "Draft" },
      sent: { color: "bg-blue-100 text-blue-700", icon: Send, label: "Sent" },
      accepted: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle, label: "Accepted" },
      declined: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Declined" },
      awarded: { color: "bg-purple-100 text-purple-700", icon: CheckCircle, label: "Awarded" },
      cancelled: { color: "bg-slate-100 text-slate-500", icon: XCircle, label: "Cancelled" },
      expired: { color: "bg-amber-100 text-amber-700", icon: AlertTriangle, label: "Expired" }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon size={12} />
        {badge.label}
      </Badge>
    );
  };

  const filteredOffers = offers.filter(offer => {
    if (statusFilter !== "all" && offer.status !== statusFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-slate-900 tracking-tight">Job Offers</h1>
          <p className="text-slate-500 font-ibm mt-1">Create and manage subcontractor job offers</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus size={16} className="mr-2" />
          Create Offer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {["draft", "sent", "accepted", "declined", "awarded"].map(status => (
          <Card key={status} className={statusFilter === status ? "ring-2 ring-primary" : ""}>
            <CardContent className="pt-4 cursor-pointer" onClick={() => setStatusFilter(statusFilter === status ? "all" : status)}>
              <p className="text-sm text-slate-500 capitalize">{status}</p>
              <p className="text-2xl font-bold">
                {offers.filter(o => o.status === status).length}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Offers List */}
      <div className="space-y-3">
        {filteredOffers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No job offers found</p>
              <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                <Plus size={16} className="mr-2" />
                Create Your First Offer
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredOffers.map((offer) => (
            <Card key={offer.offer_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewOffer(offer)}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-slate-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{offer.trade_phase} - {offer.subcontractor_name}</h3>
                      <p className="text-sm text-slate-500">{offer.project_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">${offer.total_price?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{offer.price_type}</p>
                    </div>
                    {getStatusBadge(offer.status)}
                    {offer.status === "draft" && (
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); handleSendOffer(offer.offer_id); }}>
                        <Send size={14} className="mr-1" />
                        Send
                      </Button>
                    )}
                    <ChevronRight className="text-slate-400" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Offer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Job Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Project *</label>
              <Select value={newOffer.project_id} onValueChange={(v) => setNewOffer({ ...newOffer, project_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Subcontractor *</label>
              <Select value={newOffer.subcontractor_id} onValueChange={(v) => setNewOffer({ ...newOffer, subcontractor_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select subcontractor" />
                </SelectTrigger>
                <SelectContent>
                  {subcontractors.filter(s => s.status === "active").map(s => (
                    <SelectItem key={s.subcontractor_id} value={s.subcontractor_id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Trade/Phase *</label>
              <Select value={newOffer.trade_phase} onValueChange={(v) => setNewOffer({ ...newOffer, trade_phase: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trade" />
                </SelectTrigger>
                <SelectContent>
                  {trades.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Scope of Work</label>
              <Textarea
                value={newOffer.scope_text}
                onChange={(e) => setNewOffer({ ...newOffer, scope_text: e.target.value })}
                placeholder="Describe the work to be performed..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={newOffer.proposed_start_date}
                  onChange={(e) => setNewOffer({ ...newOffer, proposed_start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={newOffer.proposed_end_date}
                  onChange={(e) => setNewOffer({ ...newOffer, proposed_end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Price Type</label>
                <Select value={newOffer.price_type} onValueChange={(v) => setNewOffer({ ...newOffer, price_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                    <SelectItem value="unit">Unit Price</SelectItem>
                    <SelectItem value="t_and_m">Time & Materials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Total Price ($)</label>
                <Input
                  type="number"
                  value={newOffer.total_price}
                  onChange={(e) => setNewOffer({ ...newOffer, total_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Retainage (%)</label>
              <Input
                type="number"
                value={newOffer.retainage_percent}
                onChange={(e) => setNewOffer({ ...newOffer, retainage_percent: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <Textarea
                value={newOffer.notes}
                onChange={(e) => setNewOffer({ ...newOffer, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateOffer}>Create Offer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Offer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedOffer && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedOffer.trade_phase}</DialogTitle>
                    <p className="text-sm text-slate-500 mt-1">{selectedOffer.project?.name}</p>
                  </div>
                  {getStatusBadge(selectedOffer.status)}
                </div>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Subcontractor */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Building2 className="text-slate-400" />
                  <div>
                    <p className="font-medium">{selectedOffer.subcontractor?.company_name}</p>
                    <p className="text-sm text-slate-500">{selectedOffer.subcontractor?.email}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="text-slate-400" size={18} />
                    <div>
                      <p className="text-sm text-slate-500">Total Price</p>
                      <p className="font-semibold">${selectedOffer.total_price?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="text-slate-400" size={18} />
                    <div>
                      <p className="text-sm text-slate-500">Timeline</p>
                      <p className="font-semibold">
                        {selectedOffer.proposed_start_date || "TBD"} - {selectedOffer.proposed_end_date || "TBD"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scope */}
                {selectedOffer.scope_text && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Scope of Work</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">
                      {selectedOffer.scope_text}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedOffer.status === "draft" && (
                    <Button onClick={() => { handleSendOffer(selectedOffer.offer_id); setViewDialogOpen(false); }}>
                      <Send size={16} className="mr-2" />
                      Send Offer
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobOffersPage;
